import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Heart, CloudOff, UtensilsCrossed } from "lucide-react";
import { ThemeContext } from "../App";
import Modal from "../components/Modal";
import { EmptyState } from "../components/common/Common";
import { fetchRecipeDetails } from "../api/recipes";
import { useFavorites } from "../utils/favorites";
import "./Categories.css";

const excludedWords = ["beef"];

const toast = (icon, title) =>
  Swal.fire({ toast: true, position: "bottom", icon, title, showConfirmButton: false, timer: 1500 });

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [meals, setMeals] = useState([]);
  const [mealsLoading, setMealsLoading] = useState(false);
  const [mealsError, setMealsError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recipeData, setRecipeData] = useState(null);
  const { theme } = useContext(ThemeContext);
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();

  const loadCategories = useCallback(() => {
    setCategoriesLoading(true);
    setCategoriesError(false);
    axios
      .get("https://www.themealdb.com/api/json/v1/1/categories.php")
      .then((response) => {
        setCategories(
          response.data.categories.filter(
            (category) =>
              !excludedWords.some((word) =>
                category.strCategory.toLowerCase().includes(word.toLowerCase())
              )
          )
        );
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
        setCategoriesError(true);
      })
      .finally(() => setCategoriesLoading(false));
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const loadMeals = useCallback((category) => {
    setMealsLoading(true);
    setMealsError(false);
    setMeals([]);
    axios
      .get(
        `https://www.themealdb.com/api/json/v1/1/filter.php?c=${category.strCategory}`
      )
      .then((response) => {
        setMeals(
          (response.data.meals || []).filter(
            (meal) =>
              !excludedWords.some((word) =>
                meal.strMeal.toLowerCase().includes(word.toLowerCase())
              )
          )
        );
      })
      .catch((error) => {
        console.error("Error fetching meals:", error);
        setMealsError(true);
      })
      .finally(() => setMealsLoading(false));
  }, []);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    loadMeals(category);
  };

  const toggleFavorite = (meal) => {
    if (isFavorite(meal.idMeal)) {
      removeFavorite(meal.idMeal);
      toast("success", `${meal.strMeal} removed from favorites`);
    } else {
      addFavorite({ ...meal, strCategory: selectedCategory?.strCategory });
      toast("success", `${meal.strMeal} added to favorites`);
    }
  };

  const viewRecipe = async (mealId) => {
    try {
      setRecipeData(await fetchRecipeDetails({ id: mealId, isMealDB: true }));
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching recipe details:", error);
      Swal.fire("Error", "Failed to load recipe details.", "error");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setRecipeData(null);
  };

  const backToCategories = () => {
    setSelectedCategory(null);
    setMeals([]);
  };

  return (
    <div className={`categories-container ${theme ? "dark" : ""}`}>
      {!selectedCategory ? (
        <>
          <header className="cat-header">
            <span className="cat-eyebrow">Browse by course</span>
            <h1>Meal Categories</h1>
          </header>

          {categoriesLoading && (
            <div className="categories-list" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <div className="skeleton-card" key={i}>
                  <div className="skeleton-img" />
                  <div className="skeleton-line" />
                </div>
              ))}
            </div>
          )}

          {categoriesError && (
            <EmptyState
              icon={CloudOff}
              title="Couldn't load categories"
              text="Check your connection and try again."
              action={{ label: "Try again", onClick: loadCategories }}
            />
          )}

          <div className="categories-list">
            {categories.map((category) => (
              <div
                key={category.idCategory}
                className="category-card"
                role="button"
                tabIndex={0}
                onClick={() => handleCategoryClick(category)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  (e.preventDefault(), handleCategoryClick(category))
                }
              >
                <img
                  src={category.strCategoryThumb}
                  alt={category.strCategory}
                  loading="lazy"
                />
                <h3>{category.strCategory}</h3>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <button onClick={backToCategories} className="back-btn">
            &larr; Back to Categories
          </button>
          <header className="cat-header">
            <span className="cat-eyebrow">Category</span>
            <h1>{selectedCategory.strCategory}</h1>
          </header>

          {mealsLoading && (
            <div className="meals-list" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <div className="skeleton-card" key={i}>
                  <div className="skeleton-img" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line short" />
                </div>
              ))}
            </div>
          )}

          {mealsError && (
            <EmptyState
              icon={CloudOff}
              title="Couldn't load meals"
              text="Check your connection and try again."
              action={{ label: "Try again", onClick: () => loadMeals(selectedCategory) }}
              secondary={{ label: "Back", onClick: backToCategories }}
            />
          )}

          {!mealsLoading && !mealsError && meals.length === 0 && (
            <EmptyState
              icon={UtensilsCrossed}
              title="No meals in this category"
              text="Try another category."
              action={{ label: "Back to Categories", onClick: backToCategories }}
            />
          )}

          <div className="meals-list">
            {meals.map((meal) => {
              const saved = isFavorite(meal.idMeal);
              return (
                <div key={meal.idMeal} className="meal-card">
                  <img
                    src={meal.strMealThumb}
                    alt={meal.strMeal}
                    className="meal-img"
                    loading="lazy"
                  />
                  <div className="meal-body">
                    <h3 className="meal-title">{meal.strMeal}</h3>
                    <div className="meal-actions">
                      <button
                        onClick={() => toggleFavorite(meal)}
                        className={`meal-btn fav ${saved ? "saved" : ""}`}
                        aria-pressed={saved}
                      >
                        <Heart size={15} fill={saved ? "currentColor" : "none"} />{" "}
                        {saved ? "Saved" : "Favorite"}
                      </button>
                      <button
                        onClick={() => viewRecipe(meal.idMeal)}
                        className="meal-btn view"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        recipe={recipeData}
      />
    </div>
  );
};

export default Categories;
